import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "export-history__header" }

import Card from 'vcomponents/common/card/Card.vue';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import TablePagination from 'vcomponents/common/table/TablePagination.vue';
import ExportProgressColumn from 'vcomponents/listing/tableColumns/ExportProgressColumn.vue';
import useListingNames from 'vcomponents/listing/apiListing/mapping/ListingNames';
import {
    searchExports,
    getExport,
    getMetadata,
    rescheduleExport,
    exportList,
} from 'api/notification/Export';
import type { ExportItem, ExportOwner } from 'api/notification/Export';
import vueFilters from 'filters/vue_filters';
import moment from 'moment';
import { ref, onMounted, onUnmounted, inject } from 'vue';
import { useStore } from 'vuex';
import type { Translator } from 'types/injects/translator.type';
import type { PushToast } from 'types/injects/pushToast.type';

const PER_PAGE = 25;

interface PaginationUpdate {
    limit: number;
    offset: number;
    total: number;
    currentPage: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ExportHistory',
  setup(__props) {

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;
const store = useStore();

const listingNames = useListingNames($t);

const exportItems = ref<ExportItem[]>([]);
const owners = ref<ExportOwner[]>([]);
const isLoading = ref(false);
const isExporting = ref(false);
const pagination = ref({
    limit: PER_PAGE,
    offset: 0,
    total: 0,
    currentPage: 1,
});

const translations: Record<string, string> = {
    date: $t.__('export.listing.columns.date'),
    name: $t.__('export.listing.columns.name'),
    collaborator: $t.__('export.listing.columns.collaborator'),
    rowCount: $t.__('export.listing.columns.rowCount'),
    progress: $t.__('export.listing.columns.progress'),
};

const formatDate = (date: string): string => {
    return vueFilters.dateFormatter(date, 'SLASHED_DATE_TIME');
};

const getExportName = (row: ExportItem): string => {
    if (row.custom_name) {
        return row.custom_name;
    }

    const date = vueFilters.dateFormatter(row.created_at, 'SLASHED_DATE_TIME', false);
    const resolvedName = listingNames[row.listing_name]
        ? listingNames[row.listing_name].toLowerCase()
        : row.listing_name;

    return $t.__('export.notifications.exportName', { type: resolvedName, date });
};

const getOwnerFullName = (ownerId: string): string => {
    const owner = owners.value.find((owner) => owner.id === ownerId);
    return owner ? `${owner.firstname} ${owner.lastname}` : '';
};

const fetchExports = async (page: number = 1, limit: number = PER_PAGE): Promise<void> => {
    isLoading.value = true;
    try {
        const response = await searchExports({
            offset: limit * (page - 1),
            limit,
            order: 'created_at',
            direction: 'desc',
        });

        exportItems.value = response.data.data;

        pagination.value = {
            limit,
            offset: (page - 1) * limit,
            total: response.data.pagination.total,
            currentPage: page,
        };
    } catch {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoading.value = false;
    }
};

const fetchMetadata = async (): Promise<void> => {
    try {
        const response = await getMetadata();
        owners.value = response.data.owners;
    } catch {
        // metadata is non-critical
    }
};

const handlePaginationChange = (newPagination: PaginationUpdate): void => {
    pagination.value = {
        ...newPagination,
        offset: (newPagination.currentPage - 1) * newPagination.limit,
    };
    fetchExports(newPagination.currentPage, newPagination.limit);
};

const handleExport = async (): Promise<void> => {
    isExporting.value = true;
    try {
        await exportList();
        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            text: $t.__('export.history.processing'),
            icon: true,
        });
        fetchExports(1);
    } catch {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            text: $t.__('export.history.launchError'),
            icon: true,
        });
    } finally {
        isExporting.value = false;
    }
};

const handleDownload = (url: string): void => {
    window.open(url, '_blank');
};

const handleReschedule = async (id: string): Promise<void> => {
    try {
        await rescheduleExport(id);
        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            text: $t.__('export.history.processing'),
            icon: true,
        });
        fetchExports(1);
    } catch (error: unknown) {
        const axiosError = error as { response?: { status: number } };
        if (axiosError.response && axiosError.response.status === 423) {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                text: $t.__('export.history.alreadyRunning'),
                icon: true,
            });
        } else {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                text: $t.__('export.history.launchError'),
                icon: true,
            });
        }
    }
};

const openRenameModal = (row: ExportItem): void => {
    store.dispatch('popupPanel/open', {
        key: 'exportRenameModal',
        props: {
            exportId: row.id,
            exportName: getExportName(row),
            callback: () => fetchExports(pagination.value.currentPage, pagination.value.limit),
        },
    });
};

const onProgressChange = async ({ id }: { id: string }): Promise<void> => {
    const index = exportItems.value.findIndex((exportItem) => exportItem.id === id);
    if (index === -1) {
        return;
    }

    try {
        const response = await getExport(id);
        exportItems.value[index] = response.data;
    } catch {
        // silent — SSE will retry
    }
};

const onExport = (): void => {
    fetchExports(1);
};

onMounted(() => {
    fetchMetadata();
    fetchExports();
    EventBus.on('listing:export', onExport as (...args: unknown[]) => void);
    EventBus.on('export:progress', onProgressChange as (...args: unknown[]) => void);
});

onUnmounted(() => {
    EventBus.off('listing:export', onExport as (...args: unknown[]) => void);
    EventBus.off('export:progress', onProgressChange as (...args: unknown[]) => void);
});

return (_ctx: any,_cache: any) => {
  const _component_CompassButton = _resolveComponent("CompassButton")!
  const _component_CompassTableHeadCell = _resolveComponent("CompassTableHeadCell")!
  const _component_CompassTableHead = _resolveComponent("CompassTableHead")!
  const _component_CompassTableBodyCell = _resolveComponent("CompassTableBodyCell")!
  const _component_CompassLink = _resolveComponent("CompassLink")!
  const _component_CompassIconButton = _resolveComponent("CompassIconButton")!
  const _component_CompassMenuItem = _resolveComponent("CompassMenuItem")!
  const _component_CompassMenu = _resolveComponent("CompassMenu")!
  const _component_CompassTableRow = _resolveComponent("CompassTableRow")!
  const _component_CompassTableBody = _resolveComponent("CompassTableBody")!
  const _component_CompassTable = _resolveComponent("CompassTable")!
  const _component_CompassEmptyState = _resolveComponent("CompassEmptyState")!

  return (_openBlock(), _createBlock(Card, { class: "export-history" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(Heading, {
          labelTitle: _unref($t).__('export.history.title'),
          variant: "h2",
          prefixColoredIcon: {
                    icon: 'history',
                    color: _ctx.CompassEnums.ColoredIconColorType.BRAND,
                }
        }, null, 8 /* PROPS */, ["labelTitle", "prefixColoredIcon"]),
        _createVNode(_component_CompassButton, {
          label: _unref($t).__('common.actions.export_the_list'),
          type: _ctx.CompassEnums.ButtonType.SECONDARY,
          loading: isExporting.value,
          onClick: handleExport
        }, null, 8 /* PROPS */, ["label", "type", "loading"])
      ]),
      (isLoading.value && exportItems.value.length === 0)
        ? (_openBlock(), _createBlock(TableLoading, { key: 0 }))
        : (exportItems.value.length > 0)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (isLoading.value)
                ? (_openBlock(), _createBlock(TableLoading, { key: 0 }))
                : (_openBlock(), _createBlock(_component_CompassTable, {
                    key: 1,
                    isCheckable: false,
                    border: false
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_CompassTableHead, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_CompassTableHeadCell, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(translations.date), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                          }),
                          _createVNode(_component_CompassTableHeadCell, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(translations.name), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                          }),
                          _createVNode(_component_CompassTableHeadCell, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(translations.collaborator), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                          }),
                          _createVNode(_component_CompassTableHeadCell, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(translations.rowCount), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                          }),
                          _createVNode(_component_CompassTableHeadCell, null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(translations.progress), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                          }),
                          _createVNode(_component_CompassTableHeadCell, {
                            id: "actionColumn",
                            fixed: "right"
                          })
                        ]),
                        _: 1 /* STABLE */
                      }),
                      _createVNode(_component_CompassTableBody, null, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(exportItems.value, (row) => {
                            return (_openBlock(), _createBlock(_component_CompassTableRow, {
                              key: row.id
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_CompassTableBodyCell, null, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(formatDate(row.created_at)), 1 /* TEXT */)
                                  ]),
                                  _: 2 /* DYNAMIC */
                                }, 1024 /* DYNAMIC_SLOTS */),
                                _createVNode(_component_CompassTableBodyCell, null, {
                                  default: _withCtx(() => [
                                    (row.public_url)
                                      ? (_openBlock(), _createBlock(_component_CompassLink, {
                                          key: 0,
                                          label: getExportName(row),
                                          link: row.public_url,
                                          target: "_blank"
                                        }, null, 8 /* PROPS */, ["label", "link"]))
                                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                          _createTextVNode(_toDisplayString(getExportName(row)), 1 /* TEXT */)
                                        ], 64 /* STABLE_FRAGMENT */))
                                  ]),
                                  _: 2 /* DYNAMIC */
                                }, 1024 /* DYNAMIC_SLOTS */),
                                _createVNode(_component_CompassTableBodyCell, null, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(getOwnerFullName(row.owner_id)), 1 /* TEXT */)
                                  ]),
                                  _: 2 /* DYNAMIC */
                                }, 1024 /* DYNAMIC_SLOTS */),
                                _createVNode(_component_CompassTableBodyCell, null, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(row.total_rows ?? ''), 1 /* TEXT */)
                                  ]),
                                  _: 2 /* DYNAMIC */
                                }, 1024 /* DYNAMIC_SLOTS */),
                                _createVNode(_component_CompassTableBodyCell, null, {
                                  default: _withCtx(() => [
                                    _createVNode(ExportProgressColumn, {
                                      status: row.status,
                                      progress: row.progress ?? 0,
                                      started_at: row.started_at ? _unref(moment)(row.started_at) : undefined
                                    }, null, 8 /* PROPS */, ["status", "progress", "started_at"])
                                  ]),
                                  _: 2 /* DYNAMIC */
                                }, 1024 /* DYNAMIC_SLOTS */),
                                _createVNode(_component_CompassTableBodyCell, { idColumn: "actionColumn" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_CompassMenu, {
                                      placement: "bottom-end",
                                      customClass: "export-history-action-menu"
                                    }, {
                                      dropdown: _withCtx(() => [
                                        _createVNode(_component_CompassMenuItem, {
                                          label: _unref($t).__('export.listing.actions.rename'),
                                          prefixIcon: "edit",
                                          onClick: ($event: any) => (openRenameModal(row))
                                        }, null, 8 /* PROPS */, ["label", "onClick"]),
                                        (row.public_url)
                                          ? (_openBlock(), _createBlock(_component_CompassMenuItem, {
                                              key: 0,
                                              label: _unref($t).__('export.notifications.download'),
                                              prefixIcon: "download",
                                              onClick: ($event: any) => (handleDownload(row.public_url ?? ''))
                                            }, null, 8 /* PROPS */, ["label", "onClick"]))
                                          : _createCommentVNode("v-if", true),
                                        _createVNode(_component_CompassMenuItem, {
                                          label: _unref($t).__('export.listing.actions.reschedule'),
                                          prefixIcon: "sync",
                                          onClick: ($event: any) => (handleReschedule(row.id))
                                        }, null, 8 /* PROPS */, ["label", "onClick"])
                                      ]),
                                      default: _withCtx(() => [
                                        _createVNode(_component_CompassIconButton, {
                                          icon: "action",
                                          size: _ctx.CompassEnums.IconButtonSize.SMALL
                                        }, null, 8 /* PROPS */, ["size"])
                                      ]),
                                      _: 2 /* DYNAMIC */
                                    }, 1024 /* DYNAMIC_SLOTS */)
                                  ]),
                                  _: 2 /* DYNAMIC */
                                }, 1024 /* DYNAMIC_SLOTS */)
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1024 /* DYNAMIC_SLOTS */))
                          }), 128 /* KEYED_FRAGMENT */))
                        ]),
                        _: 1 /* STABLE */
                      })
                    ]),
                    _: 1 /* STABLE */
                  })),
              _withDirectives(_createVNode(TablePagination, {
                isLoading: isLoading.value,
                pagination: pagination.value,
                onUpdatePagination: handlePaginationChange
              }, null, 8 /* PROPS */, ["isLoading", "pagination"]), [
                [_vShow, !isLoading.value]
              ])
            ], 64 /* STABLE_FRAGMENT */))
          : (!isLoading.value)
            ? (_openBlock(), _createBlock(_component_CompassEmptyState, {
                key: 2,
                illustrationPath: "/ressources/logged/img/illustrations/no-results.svg",
                title: _unref($t).__('export.listing.emptyPlaceholder')
              }, null, 8 /* PROPS */, ["title"]))
            : _createCommentVNode("v-if", true)
    ]),
    _: 1 /* STABLE */
  }))
}
}

})